import type { CloudSharedVolume, CloudSharedVolumeEntry } from "@/lib/api";
import type { CloudSharedVolumeEntryPayload } from "@/lib/dnd/payload";
import { splitPath } from "@/lib/environment-files";

/** 読み込んだディレクトリの直下 (頁を読み足した分まで)。 */
export interface CloudSharedVolumeDirectory {
	entries: CloudSharedVolumeEntry[];
	nextCursor: string | null;
}

/** ボリュームの根 (path は空文字) か、その下の 1 件の行。 */
export interface CloudSharedVolumeEntryRow {
	type: "entry";
	key: string;
	volumeId: string;
	path: string;
	name: string;
	depth: number;
	directory: boolean;
	/** 開ける (ディレクトリか、mount されているボリュームの根)。 */
	expandable: boolean;
	expanded: boolean;
	entry: CloudSharedVolumeEntry | null;
	volume: CloudSharedVolume;
}

/** ディレクトリの直下の続きを読む行。 */
interface CloudSharedVolumeLoadMoreRow {
	type: "loadMore";
	key: string;
	volumeId: string;
	path: string;
	depth: number;
}

type CloudSharedVolumeRow = CloudSharedVolumeEntryRow | CloudSharedVolumeLoadMoreRow;

/** 行とディレクトリのキー (ボリュームの根はボリュームの ID)。 */
export function cloudSharedVolumeRowKey(volumeId: string, path: string): string {
	return path === "" ? volumeId : `${volumeId}/${path}`;
}

/** 環境の中の絶対パス (根は mountPath)。 */
export function cloudSharedVolumeContainerPath(volume: CloudSharedVolume, path: string): string {
	return path === "" ? volume.mountPath : `${volume.mountPath}/${path}`;
}

/** シンボリックリンク・特殊なファイルは、辿らず開かない。 */
export function isCloudSharedVolumeEntryUnsupported(entry: CloudSharedVolumeEntry | null): boolean {
	return entry !== null && (entry.kind === "symlink" || entry.kind === "other");
}

/**
 * 行に置くときのディレクトリ (根からのパス)。根とフォルダの行はその中、ファイルの行は親。mount されていないボリュームと、
 * シンボリックリンク・特殊なファイルの行は null。
 */
export function cloudSharedVolumeDropDirectory(row: CloudSharedVolumeEntryRow): string | null {
	if (!row.volume.mounted || isCloudSharedVolumeEntryUnsupported(row.entry)) return null;
	return row.directory ? row.path : splitPath(row.path).dir;
}

/** ボリュームを最上位のフォルダにし、展開して読み込んだディレクトリの中身を描画順に並べる。 */
export function buildCloudSharedVolumeRows(
	volumes: readonly CloudSharedVolume[],
	directories: ReadonlyMap<string, CloudSharedVolumeDirectory>,
	expanded: ReadonlySet<string>,
): CloudSharedVolumeRow[] {
	const rows: CloudSharedVolumeRow[] = [];
	const appendChildren = (volume: CloudSharedVolume, path: string, depth: number) => {
		const dirKey = cloudSharedVolumeRowKey(volume.volumeId, path);
		const directory = directories.get(dirKey);
		if (!directory) return;
		for (const entry of directory.entries) {
			const key = cloudSharedVolumeRowKey(volume.volumeId, entry.path);
			const isDirectory = entry.kind === "directory";
			// 中身を捨てたフォルダは閉じた形にし、押せば読み直す。
			const isExpanded = isDirectory && expanded.has(key) && directories.has(key);
			rows.push({
				type: "entry",
				key,
				volumeId: volume.volumeId,
				path: entry.path,
				name: entry.name,
				depth,
				directory: isDirectory,
				expandable: isDirectory,
				expanded: isExpanded,
				entry,
				volume,
			});
			if (isExpanded) appendChildren(volume, entry.path, depth + 1);
		}
		if (directory.nextCursor !== null) {
			rows.push({
				type: "loadMore",
				key: `${dirKey}\n続き`,
				volumeId: volume.volumeId,
				path,
				depth,
			});
		}
	};
	for (const volume of volumes) {
		const key = cloudSharedVolumeRowKey(volume.volumeId, "");
		const isExpanded = volume.mounted && expanded.has(key);
		rows.push({
			type: "entry",
			key,
			volumeId: volume.volumeId,
			path: "",
			name: volume.name,
			depth: 0,
			directory: true,
			expandable: volume.mounted,
			expanded: isExpanded,
			entry: null,
			volume,
		});
		if (isExpanded) appendChildren(volume, "", 1);
	}
	return rows;
}

/** ツリーで選んでいる共有ボリュームの行。directory は新しく作る・置く先 (根からのパス)。作れない行なら null。 */
export interface CloudSharedVolumeSelection {
	volumeId: string;
	directory: string | null;
}

/** 右クリックに出す書く操作。canEdit はエージェント管理の権限があり、環境が起動していること。 */
export function cloudSharedVolumeEditActions(
	row: CloudSharedVolumeEntryRow,
	canEdit: boolean,
): { create: boolean; renameAndDelete: boolean } {
	return {
		create: canEdit && cloudSharedVolumeDropDirectory(row) !== null,
		renameAndDelete: canEdit && row.volume.mounted && row.entry !== null,
	};
}

export const CLOUD_SHARED_VOLUME_DELETE_WARNING =
	"共有ボリュームから削除します。このボリュームを使う他のエージェントからも消え、元に戻せません。";

/** 削除の確認文。rows は一番上のものだけ。 */
export function buildCloudSharedVolumeDeleteMessage(
	rows: readonly Pick<CloudSharedVolumeEntryRow, "name" | "directory">[],
): string {
	if (rows.length !== 1) return `選択した${rows.length}件を${CLOUD_SHARED_VOLUME_DELETE_WARNING}`;
	const [row] = rows;
	return row.directory
		? `フォルダ「${row.name}」とその中身を${CLOUD_SHARED_VOLUME_DELETE_WARNING}`
		: `「${row.name}」を${CLOUD_SHARED_VOLUME_DELETE_WARNING}`;
}

/**
 * 下にあるもの (祖先も含まれているもの) を除く。~ のパスと違い、前後の空白を削らない (別のものを指してしまう)。
 */
export function cloudSharedVolumeTopLevelPaths(paths: readonly string[]): string[] {
	const unique = [...new Set(paths)];
	return unique.filter((path) => !unique.some((other) => path.startsWith(`${other}/`)));
}

/** destination (根からのパス) へ移すと実際に動くもの (一番上のもので、親が destination でないもの)。 */
export function cloudSharedVolumeMovablePaths(
	paths: readonly string[],
	destination: string,
): string[] {
	return cloudSharedVolumeTopLevelPaths(paths).filter(
		(path) => splitPath(path).dir !== destination,
	);
}

/** 選んだ行を消すときの要求 (一番上のものだけ) と確認文。根・mount されていないもの・別のボリュームが混ざれば null。 */
export function cloudSharedVolumeDeleteRequest(
	rows: readonly CloudSharedVolumeEntryRow[],
): { volumeId: string; paths: string[]; message: string } | null {
	const volumeId = rows[0]?.volumeId;
	if (
		volumeId === undefined ||
		rows.some((row) => row.entry === null || !row.volume.mounted || row.volumeId !== volumeId)
	)
		return null;
	const paths = cloudSharedVolumeTopLevelPaths(rows.map((row) => row.path));
	const topLevel = rows.filter((row) => paths.includes(row.path));
	return { volumeId, paths, message: buildCloudSharedVolumeDeleteMessage(topLevel) };
}

/** ドラッグした行を、落とした行 (フォルダはその中、ファイルは親、根は根) へ移せるか。同じボリュームの中だけ。 */
export function canMoveCloudSharedVolumeEntries(
	payload: CloudSharedVolumeEntryPayload,
	row: CloudSharedVolumeEntryRow,
): boolean {
	if (payload.volumeId !== row.volumeId) return false;
	const directory = cloudSharedVolumeDropDirectory(row);
	if (directory === null) return false;
	const movable = cloudSharedVolumeMovablePaths(
		payload.entries.map((entry) => entry.path),
		directory,
	);
	return (
		movable.length > 0 &&
		movable.every((path) => path !== directory && !directory.startsWith(`${path}/`))
	);
}

/**
 * 読み込んだ中に、mount されたボリュームの directory (根からのパス) がまだあるか。作成・アップロードの先を決めた後に、
 * 取り直しで消えていないかを確かめる。
 */
export function isCloudSharedVolumeDirectoryLoaded(
	volumes: readonly CloudSharedVolume[] | null,
	directories: ReadonlyMap<string, CloudSharedVolumeDirectory>,
	volumeId: string,
	directory: string,
): boolean {
	if (!(volumes ?? []).some((volume) => volume.volumeId === volumeId && volume.mounted))
		return false;
	if (directory === "") return true;
	const { dir, name } = splitPath(directory);
	return (
		directories
			.get(cloudSharedVolumeRowKey(volumeId, dir))
			?.entries.some((entry) => entry.name === name && entry.kind === "directory") ?? false
	);
}

/** ペインの新規作成・アップロードの先。 */
export type FileTreeCreateTarget =
	| { kind: "home"; parentPath: string }
	| { kind: "sharedVolume"; volumeId: string; directory: string };

/**
 * 共有ボリュームの行を選んでいればその行 (フォルダはその中、ファイルは親) に、そうでなければ ~ の homeParentPath に作る。
 * 選んでいる共有ボリュームの行に作れなければ (権限が無い・mount されていないなど) null。
 */
export function resolveFileTreeCreateTarget(
	selection: CloudSharedVolumeSelection | null,
	homeParentPath: string,
): FileTreeCreateTarget | null {
	if (selection === null) return { kind: "home", parentPath: homeParentPath };
	return selection.directory === null
		? null
		: { kind: "sharedVolume", volumeId: selection.volumeId, directory: selection.directory };
}
